import React from 'react';
import classnames from 'classnames';
import { HidingTooltip } from "ui/Tooltip";
import CopyIcon from 'ui/Icons/copy.component.svg';
import { IBookingInformationReservationTeamData } from 'services/BookingManagerApi';
import { formatDateDisplay } from 'utils/date';
import { getAge } from 'pureUi/GuestInformationForm/helpers';
import { IGuest } from 'services/BackendApi';
import { useNotifications } from 'hooks/useNotifications';

interface ICopyBookingDetailsButtonProps {
  isInventoryHeaderOpen: boolean;
  shouldApplyFadeStyle: boolean;
  setShouldApplyFadeStyle: (shouldApplyFadeStyle: boolean) => void;
  bookingInformationReservationTeamData: IBookingInformationReservationTeamData | null;
}

export const CopyBookingDetailsButton: React.FC<ICopyBookingDetailsButtonProps> = ({
  isInventoryHeaderOpen,
  shouldApplyFadeStyle,
  setShouldApplyFadeStyle,
  bookingInformationReservationTeamData,
}) => {
    const { showSuccessNotification, showErrorNotification } = useNotifications();
    const isElementOverflown = element => {
      return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
    };
    
    const getBookingInformationTooltipContent = () => (
        <div className="space-y-5">
          <span className="block space-x-2">
            <span className="font-bold">Ref:</span>
            <span>{bookingInformationReservationTeamData?.bookingReferenceNo}</span>
          </span>
          <span className="block space-x-2">
            <span className="font-bold">Hotel:</span>
            <span>{bookingInformationReservationTeamData?.hotelNames.join(', ')}</span>
          </span>
          <span className="block space-x-2">
            <span className="font-bold">Resort Confirmation:</span>
            <span>{bookingInformationReservationTeamData?.resortConfirmationNumbers.join(', ')}</span>
          </span>
          <span className="block space-x-2">
            <span className="font-bold">Dates:</span>
            <span>
              {formatDateDisplay(bookingInformationReservationTeamData?.arrivalDate)} -{' '}
              {formatDateDisplay(bookingInformationReservationTeamData?.departureDate)}
            </span>
          </span>
          <span className="block">
            <span className="block font-bold">Guests:</span>
            <span className="space-y-2">
              {bookingInformationReservationTeamData?.guests.map((guest, guestIndex) => (
                <span key={`${guestIndex}${guest.title}${guest.name}`} className="block">
                  <span>
                    {guest.name} {guest.dateOfBirth ? `- Age: ${getAge(guest as IGuest)}` : ''}{' '}
                    {guest.isLeadGuest && ' (LEAD)'}
                  </span>
                  <span className="block">
                    From {guest.arrivalDate ? formatDateDisplay(guest.arrivalDate) : ''} - {guest.arrivalFlightNo} at{' '}
                    {guest.arrivalTime} - {guest.arrivalCIPBookingNumber}
                  </span>
                  <span className="block">
                    To {guest.departureDate ? formatDateDisplay(guest.departureDate) : ''} - {guest.departureFlightNo} at{' '}
                    {guest.departureTime} - {guest.departureCIPBookingNumber}
                  </span>
                </span>
              ))}
            </span>
          </span>
          <span className="block">
            <span className="block font-bold">Accommodations:</span>
            {bookingInformationReservationTeamData?.accommodations.map(accommodation => (
              <span key={`${accommodation.title}${accommodation.title}`} className="block">
                <span>
                  {accommodation.title} - Meal basis: {accommodation.mealPlan}
                </span>
              </span>
            ))}
          </span>
          <span className="block">
            <span className="block font-bold">Transfers:</span>
            {bookingInformationReservationTeamData?.transfers.map((transfer, transferIndex) => (
              <span key={`${transfer.title}${transferIndex}`} className="block">
                <span>
                  {transfer.title} - {transfer.tertiaryText}
                </span>
              </span>
            ))}
          </span>
        </div>
      );

  // this looks insane but please don't format it! This code is formatted like this
  // so that the string it returns is formatted correctly when copied to the clipboard
  const getBookingInformationReservationTeamClipboardFormat = () => {
    return `Ref: ${bookingInformationReservationTeamData?.bookingReferenceNo || ''}
Hotel: ${bookingInformationReservationTeamData?.hotelNames || ''}
Resort Confirmation: ${bookingInformationReservationTeamData?.resortConfirmationNumbers || ''}
Dates: ${
      bookingInformationReservationTeamData?.arrivalDate
        ? formatDateDisplay(bookingInformationReservationTeamData?.arrivalDate)
        : ''
    } - ${
      bookingInformationReservationTeamData?.departureDate
        ? formatDateDisplay(bookingInformationReservationTeamData?.departureDate)
        : ''
    }

Guests:
${bookingInformationReservationTeamData?.guests
  .map(
    guest =>
      `${guest.name || ''} ${guest.dateOfBirth ? `- Age: ${getAge(guest as IGuest)}` : ``} ${
        guest.isLeadGuest ? ' (LEAD)' : ''
      }
From ${guest.arrivalDate ? formatDateDisplay(guest.arrivalDate) : ''} - ${guest.arrivalFlightNo ||
        ''} at ${guest.arrivalTime || ''} - ${guest.arrivalCIPBookingNumber || ''}
To ${guest.departureDate ? formatDateDisplay(guest.departureDate) : ''} - ${guest.departureFlightNo ||
        ''} at ${guest.departureTime || ''} - ${guest.departureCIPBookingNumber || ''}
`
  )
  .join('')}
Accommodations:
${bookingInformationReservationTeamData?.accommodations
  .map(
    accommodation => `${accommodation.title || ''} - Meal basis: ${accommodation.mealPlan || ''}
`
  )
  .join('')}
Transfers:
${bookingInformationReservationTeamData?.transfers
  .map(
    transfer => `${transfer.title || ''} - ${transfer.tertiaryText || ''}
`
  )
  .join('')}`;
  };

  return (
    <HidingTooltip
      tooltipClassname={classnames('booking-information-tooltip p-4 overflow-y-hidden', {
        'max-h-[calc(100dvh-300px)]': isInventoryHeaderOpen,
        'max-h-[calc(100dvh-320px)]': !isInventoryHeaderOpen,
        'fade-out-to-white': shouldApplyFadeStyle,
      })}
      onRenderCallback={() => {
        const bookingInformationTooltipElement = document.getElementsByClassName(
          'booking-information-tooltip'
        )[0];
        setShouldApplyFadeStyle(isElementOverflown(bookingInformationTooltipElement));
      }}
      renderTooltipContent={getBookingInformationTooltipContent}
      position="bottom-right"
      width="600px"
    >
      <button
        className="text-brown-prime h-20px cursor-pointer bg-white p-0 outline-none"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(getBookingInformationReservationTeamClipboardFormat());
            showSuccessNotification('Copied booking information to clipboard');
          } catch (e) {
            showErrorNotification('Failed to copy booking information to clipboard');
          }
        }}
      >
        <CopyIcon className="w-[20px] fill-brown-prime" />
      </button>
    </HidingTooltip>
  );
};